import React from 'react';
import { Stack, Clickable } from '@moda/om';
import ExitIcon from '@moda/icons/exit-20';
import { LineItem } from '../../hooks/useCart/cart.rest';
import { useLineItemMutations, Mode } from './useLineItemMutations';
import { LineItemActions } from './LineItemActions';
import { LineItemAlerts } from './LineItemAlerts';
import { LineItemAvailabilityNotes } from './LineItemAvailabilityNotes';
import { LineItemCustomization } from './LineItemCustomization';
import { LineItemDeliveryNotes } from './LineItemDeliveryNotes';
import { LineItemImage } from './LineItemImage';
import { LineItemOverlay } from './LineItemOverlay';
import { LineItemPrice } from './LineItemPrice';
import { LineItemProductDetails } from './LineItemProductDetails';
import { LineItemQuantitySelector } from './LineItemQuantitySelector';
import { LineItemShippingDetails } from './LineItemShippingDetails';
import { LineItemSizeAndQuantity } from './LineItemSizeAndQuantity';
import { LineItemSizeSelector } from './LineItemSizeSelector';
import { LineItemPromo } from './LineItemPromo';

import './LineItemMini.scss';

type Props = {
  granularity?: 'minimal' | 'full';
  item: LineItem;
};

export const LineItemMini: React.FC<Props> = ({ granularity = 'full', item }) => {
  const {
    handleDelete,
    handleChangeSize,
    handleChangeQuantity,
    handleChangeType,
    mode
  } = useLineItemMutations(item);

  return (
    <div className="LineItemMini" data-testid="line-item-mini">
      {granularity === 'minimal' && (
        <Clickable className="LineItemMini__remove" aria-label="Remove" onClick={handleDelete}>
          <ExitIcon />
        </Clickable>
      )}
      <Stack space={3}>
        <LineItemAvailabilityNotes
          granularity={granularity}
          item={item}
          onChangeType={handleChangeType}
        />
        <Stack space={2} direction="horizontal">
          <div className="LineItemMini__image">
            <LineItemImage item={item} />
          </div>
          <Stack className="LineItemMini__details" space={3}>
            <LineItemProductDetails item={item} />
            {granularity === 'full' && <LineItemCustomization item={item} />}
            <LineItemPrice direction="horizontal" alignItems="flex-start" item={item} />
            <Stack className="LineItemMini__selectors-container" space={2}>
              {granularity === 'full' && (
                <div className="LineItemMini__selectors">
                  <LineItemSizeSelector item={item} onChange={handleChangeSize} />
                  <LineItemQuantitySelector item={item} onChange={handleChangeQuantity} />
                </div>
              )}
              {granularity === 'minimal' && <LineItemSizeAndQuantity item={item} />}
              <LineItemAlerts item={item} />
            </Stack>
            <LineItemShippingDetails granularity={granularity} item={item} />
            <LineItemPromo item={item} />
          </Stack>
        </Stack>

        {granularity === 'full' && (
          <>
            <LineItemDeliveryNotes item={item} />
            <LineItemActions item={item} onDelete={handleDelete} onChangeType={handleChangeType} />
          </>
        )}
      </Stack>

      <LineItemOverlay item={item} mode={mode} show={mode !== Mode.Resting} />
    </div>
  );
};
